import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users } from "lucide-react";
import api from "../../lib/apiClient";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";

/**
 * DashboardRecentFollowers — fetches the producer's 5 most recent
 * followers, clears the new-follower badge on mount, and renders a
 * tappable list with a "Follow back" toggle. Self-hides when there
 * are no recent followers (newly registered or quiet creators).
 *
 * Owns its own state because the recent-followers data is only
 * consumed inside this panel — no other parent code reads it.
 *
 * Props:
 *   user  — the current viewer (for the "View all" deep-link)
 *   token — JWT; the hook bails silently when missing
 */
const DashboardRecentFollowers = ({ user, token }) => {
  const [recentFollowers, setRecentFollowers] = useState([]);
  const [followBackBusy, setFollowBackBusy] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/users/me/recent-followers?limit=5`);
        if (!cancelled) setRecentFollowers(res.data || []);
      } catch (_err) { /* ignore — empty list is the safe default */ }
      try {
        await api.post(`/notifications/mark-followers-seen`, {});
        window.dispatchEvent(new CustomEvent("followers-seen"));
      } catch (_err) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleFollowBackToggle = async (e, targetUsername, currentlyFollowing) => {
    e.preventDefault();
    e.stopPropagation();
    if (!token || !targetUsername) return;
    setFollowBackBusy(targetUsername);
    try {
      if (currentlyFollowing) {
        await api.delete(`/users/${targetUsername}/follow`);
        toast.success(`Unfollowed @${targetUsername}`);
      } else {
        await api.post(`/users/${targetUsername}/follow`, {});
        toast.success(`Following @${targetUsername} back`);
      }
      setRecentFollowers((prev) => prev.map((f) =>
        f.username === targetUsername ? { ...f, is_following_back: !currentlyFollowing } : f
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
    setFollowBackBusy(null);
  };

  if (recentFollowers.length === 0) return null;

  return (
    <div className="mb-6 sm:mb-8 bg-gray-900/60 border border-gray-800 rounded-lg p-3 sm:p-4" data-testid="recent-followers-panel">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm sm:text-base font-semibold text-white flex items-center gap-2">
          <Users className="h-4 w-4 text-emerald-400" />
          Recent Followers
        </h2>
        <Link to={`/${user.username}`} className="text-xs text-emerald-400 hover:text-emerald-300" data-testid="view-all-followers-link">
          View all →
        </Link>
      </div>
      <ul className="flex flex-col divide-y divide-gray-800">
        {recentFollowers.map((f) => {
          const when = (() => {
            if (!f.followed_at) return "";
            const diff = (Date.now() - new Date(f.followed_at).getTime()) / 1000;
            if (diff < 60) return "just now";
            if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
            if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
            if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
            return new Date(f.followed_at).toLocaleDateString();
          })();
          return (
            <li key={f.id}>
              <Link
                to={`/${f.username}`}
                className="flex items-center gap-3 py-2 hover:bg-gray-800/60 rounded px-2 -mx-2 transition-colors"
                data-testid={`recent-follower-${f.username}`}
              >
                <Avatar className="h-9 w-9 shrink-0">
                  <AvatarImage src={f.profile_image} />
                  <AvatarFallback className="bg-emerald-600 text-sm">{f.username[0].toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm font-medium truncate">@{f.username}</span>
                    {f.is_new && (
                      <span className="text-[9px] uppercase tracking-wider font-bold text-rose-400 bg-rose-500/10 px-1.5 py-0.5 rounded-full border border-rose-500/30 shrink-0">
                        New
                      </span>
                    )}
                  </div>
                  {f.bio && <p className="text-gray-500 text-xs truncate">{f.bio}</p>}
                </div>
                <span className="text-gray-500 text-xs shrink-0">{when}</span>
                <button
                  onClick={(e) => handleFollowBackToggle(e, f.username, f.is_following_back)}
                  disabled={followBackBusy === f.username}
                  data-testid={`follow-back-btn-${f.username}`}
                  className={`shrink-0 text-[10px] font-semibold px-2.5 py-1 rounded-full border transition-colors disabled:opacity-60 ${
                    f.is_following_back
                      ? "bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700"
                      : "bg-emerald-600 text-white border-emerald-500 hover:bg-emerald-500"
                  }`}
                >
                  {f.is_following_back ? "Following" : "Follow back"}
                </button>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default DashboardRecentFollowers;
